/**
 * 房间主屏 —— `App` 的 `table` slot。
 *
 * 它把三样东西缝在一起，自己不画任何牌桌元素：
 *   1. `TableScene`（已完成）负责渲染 `.stage` 与 `.dock` 两个 grid 区，
 *      也就是 App 对 `table` slot 的硬约定；
 *   2. `ActionDockBinding` 把服务端的 `ActionPrompt` 接进 dock；
 *   3. `WaitingRoom` 在人不够时盖在舞台上。
 *
 * 数据流全程单向，且只有一个真源：
 *
 *   WsTransport → useNetStore.view (PlayerView, 唯一真源)
 *                → roomMirror: buildTableVM() → tableStore.view (PublicHandState)
 *                → TableScene / useTableFx（红线 #50 的快进在这里自动成立）
 *                → ActionDockBinding（读 prompt）→ link.send(playerAction)
 *
 * ★ 这里**不建连**。建连、鉴权、重连、座位镜像都在串联层（见 index.ts 的接线说明）；
 *   房间屏只消费状态、只经 `RoomLink` 发消息。这样同一套 UI 也能被回放器复用。
 */
import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { PlayerView, SeatIndex } from '@poker/shared'

import { useUiStore } from '../store/index.js'
import { TableScene } from '../table/TableScene.js'
import { selectIsActor, selectNetView, useNetStore } from '../transport/applyMessage.js'

import { ActionDockBinding } from './ActionDockBinding.js'
import { ConnectionBar } from './ConnectionBar.js'
import { HandResultOverlay } from './NextHand.js'
import { EndGameSheet, SettlementOverlay } from './Settlement.js'
import { SeatSelfSheet } from './SeatSelfSheet.js'
import { SitDownSheet } from './SitDownSheet.js'
import { TopUpEntry, TopUpQueueSheet, TopUpSheet } from './TopUp.js'
import { SeatMenuSheet, TransferLogSheet, TransferQueueSheet, TransferSheet } from './Transfer.js'
import { WaitingRoom } from './WaitingRoom.js'
import { seatSelfActions } from './roomDerive.js'
import { hideSettledHand, startRoomVmMirror } from './roomMirror.js'
import { useRoomLink } from './roomLink.js'
import { useActorClaim } from './useActorClaim.js'
import { SayLayer } from '../talk/SayLayer.js'
import './room.css'

/** node / 老浏览器兜底：没有 rAF 时用定时器，语义一样。 */
function nextFrame(cb: () => void): void {
  const raf = globalThis.requestAnimationFrame
  if (typeof raf === 'function') raf(() => cb())
  else globalThis.setTimeout(cb, 16)
}

/**
 * 快照落地 → 开两帧 resync（全局禁 transition，§11.6）。
 *
 * ★ 重连**不补播动画**：一份快照里可能压着断线期间的 60 个事件，
 *   补播会变成动画风暴，而且播的全是过期信息。正确做法是直接落位，
 *   断线期间发生了什么由 `ConnectionBar` 的「你错过了 N 手牌」交代。
 */
function useSnapshotResync(): void {
  const setResync = useUiStore((s) => s.setResync)
  const link = useRoomLink()
  // 切回这一页（或断线重连回来）时自动把行动权要回来，见 useActorClaim 的注释
  useActorClaim(link)

  useEffect(() => {
    const sub = link.onMessage
    if (sub === undefined) return undefined
    let cancelled = false
    const un = sub((m) => {
      if (m.type !== 'roomSnapshot') return
      setResync(true)
      // 两帧：第一帧让新状态带着 `.resync` 落地，第二帧再放开动画
      nextFrame(() =>
        nextFrame(() => {
          if (!cancelled) setResync(false)
        }),
      )
    })
    return () => {
      cancelled = true
      setResync(false)
      un()
    }
  }, [link, setResync])
}

export interface RoomScreenProps {
  /** 额外的舞台覆盖层（结算卡、引导层等）。等待室由本组件自己管。 */
  readonly stageOverlay?: ReactNode
  /** 关掉行动区的预选条 */
  readonly preAction?: boolean
}

/** 单手结算与暂停等待室必须串行，不能在筹码打空时同时叠在舞台上。 */
export function waitingRoomVisible(view: PlayerView | null, confirmedResultHandId: string | null): boolean {
  if (view === null) return false
  const hand = view.hand
  const idle = hand === null || hand.phase === 'complete'
  if (!idle || (view.room.phase !== 'lobby' && view.room.phase !== 'paused')) return false
  const resultNeedsAttention =
    hand !== null &&
    hand.phase === 'complete' &&
    hand.result !== null &&
    view.readyState !== undefined &&
    view.settlement === undefined &&
    confirmedResultHandId !== hand.handId
  return !resultNeedsAttention
}

/**
 * 该从牌桌上收走的那一手（null = 照常画）。
 *
 * 等待室一露出来（暂停 / 大厅相位，且单手结算已经看过），上一手就不再属于这一屏：
 * 公共牌、底池、庄家钮、弃牌 / ALL IN、「本手结束」都是过期信息，和等待室说的
 *「等人 / 等买分」叠在一起像牌局卡在了半路。
 *
 * ★ 收走之后同一手不再摆回来（`lastHidden`）：房主点了继续、房间回到 running、
 *   新的一手还没发下来的那一两秒里，等待室先消失了 —— 只看「等待室在不在」的话，
 *   上一手会闪回牌桌一下。直到下一手发下来（handId 变了）才恢复正常。
 */
export function settledHandToHide(
  view: PlayerView | null,
  waitingVisible: boolean,
  lastHidden: string | null,
): string | null {
  const hand = view?.hand ?? null
  if (hand === null || hand.phase !== 'complete') return null
  if (waitingVisible) return hand.handId
  return lastHidden === hand.handId ? lastHidden : null
}

/** 操作区在一手刚打完时的状态字。收走了的那一手（暂停等待中）不再说「本手结束」 */
export function handEndStatus(view: PlayerView | null, hiddenHandId: string | null): string | null {
  return view?.hand?.phase === 'complete' && hiddenHandId === null ? '本手结束' : null
}

/**
 * 点自己的座位能不能打开「我的座位」面板。
 *
 * - 要有行动权：暂离 / 换座 / 起立在服务端都只认操作窗口，影子页点了只会被拒；
 * - ★ 轮到自己时不响应：面板会盖住一个正在倒计时的决定（D6）。那几秒里自己那一格退回纯展示。
 */
export function heroSeatTappable(view: PlayerView | null, isActor: boolean): boolean {
  if (!isActor || seatSelfActions(view) === null || view === null) return false
  const hand = view.hand
  const myTurn = hand !== null && hand.phase !== 'complete' && hand.actorSeat === view.viewer.ownedSeat
  return !myTurn && view.prompt === null
}

export function RoomScreen({ stageOverlay, preAction }: RoomScreenProps): ReactNode {
  const view = useNetStore(selectNetView)
  const isActor = useNetStore(selectIsActor)
  const unveil = useUiStore((s) => s.unveil)
  const openSheet = useUiStore((s) => s.openSheet)
  const [confirmedResultHandId, setConfirmedResultHandId] = useState<string | null>(null)

  /**
   * 点别人的座位牌 → 座位名片（向 TA 买筹码；房主点 AI 还有「移出」）。
   * 稳定引用：`Seat` 是 memo 的，每帧换一个新函数会让整排座位跟着重渲染。
   */
  const onSeatSelect = useCallback(
    (seat: SeatIndex): void => {
      openSheet('seatMenu', seat)
    },
    [openSheet],
  )
  /** 点自己的座位 → 「我的座位」面板。同样要稳定引用 */
  const onHeroSelect = useCallback((): void => {
    openSheet('seatSelf')
  }, [openSheet])
  const heroTappable = heroSeatTappable(view, isActor)

  // 渲染模型镜像：net(PlayerView) → tableStore(PublicHandState)
  useEffect(() => startRoomVmMirror(), [])
  useSnapshotResync()

  /**
   * ★ 联机模式必须主动揭开遮牌页。
   * `uiStore.veiled` 的初值是 `true`（红线 #12：本地传手机模式刷新后绝不能直接
   * 露出上一个人的底牌）。但联机模式下每人一台设备，根本不存在「传手机」，
   * 不揭开的话进房间只会看到一块纯色遮罩，而且看不出是 bug 还是在加载。
   */
  useLayoutEffect(() => {
    unveil()
  }, [unveil])

  // 等待室与单手结算的先后关系统一收敛在 waitingRoomVisible，避免筹码打空时双层叠加。
  // 筹码打空会同时进入 paused 和单手结算：先看完结果，点「继续」后才露出补码等待室。
  const waiting = waitingRoomVisible(view, confirmedResultHandId)
  // 等待室露出来之后，上一手从牌桌上收走（见 settledHandToHide）
  const [lastHidden, setLastHidden] = useState<string | null>(null)
  const hiddenHandId = settledHandToHide(view, waiting, lastHidden)
  useEffect(() => {
    if (hiddenHandId !== lastHidden) setLastHidden(hiddenHandId)
  }, [hiddenHandId, lastHidden])
  // layout effect：在这一帧绘制之前就换掉牌桌内容，不先闪一帧上一手
  useLayoutEffect(() => {
    hideSettledHand(hiddenHandId)
  }, [hiddenHandId])
  useEffect(() => () => hideSettledHand(null), [])

  return (
    <TableScene
      dock={
        <ActionDockBinding
          {...(preAction === undefined ? {} : { preAction })}
          statusOverride={handEndStatus(view, hiddenHandId)}
        />
      }
      onSeatSelect={onSeatSelect}
      {...(heroTappable ? { onHeroSelect } : {})}
      stageOverlay={
        <>
          {/* 聊天气泡层：z-say(6)，在所有牌局元素之下 */}
          <SayLayer />
          {waiting && <WaitingRoom />}
          {/* 手牌结束后的结算浮层：谁赢了、什么牌型、赢了多少，以及「在等谁点继续」。
              盖在舞台上而不是全屏模态 —— 牌面与筹码要能一起看见。 */}
          <HandResultOverlay onConfirmed={setConfirmedResultHandId} />
          {/* 筹码见底才出现的买分入口。放在舞台角上而不是 dock 里：
              dock 在自己的回合上已经被行动按钮占满，多一颗按钮就是多一次误触。
              等待室开着、而抽屉已经替它说了「买分 / 等房主批准」时（暂离且筹码用完）不画；
              筹码没用完的人抽屉里没有买分入口，照画 */}
          <TopUpEntry waitingOpen={waiting} />
          {stageOverlay}
        </>
      }
    />
  )
}

/**
 * `App` 的 `overlays` slot：断线条 + 选座面板。
 *
 * 与 `RoomScreen` 分开导出是因为它们落在不同的 grid 区 ——
 * 断线条必须能盖在顶栏下方且不参与舞台的 `overflow: hidden`。
 */
export function RoomOverlays({ onLeave }: { readonly onLeave?: () => void }): ReactNode {
  return (
    <>
      <ConnectionBar {...(onLeave === undefined ? {} : { onLeave })} />
      <SitDownSheet />
      <SeatSelfSheet {...(onLeave === undefined ? {} : { onLeave })} />
      <TopUpSheet />
      <TopUpQueueSheet />
      <SeatMenuSheet />
      <TransferSheet />
      <TransferQueueSheet />
      <TransferLogSheet />
      <EndGameSheet />
      {/* 战报盖在最上层：结算完房间就关了，这一屏是这局的终点 */}
      <SettlementOverlay {...(onLeave === undefined ? {} : { onLeave })} />
    </>
  )
}

export default RoomScreen
